import { useState } from 'react';
import { Loader2, Search } from 'lucide-react';
import { Button } from '@/components/atoms/button';
import { Checkbox } from '@/components/atoms/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/atoms/dialog';
import { Input } from '@/components/atoms/input';
import { Label } from '@/components/atoms/label';
import { useContactSearch } from '@/hooks/contacts/useContactSearch';
import { useCreateContact } from '@/hooks/contacts/useCreateContact';
import {
  ROLE_LABEL,
  useAddLocationContact,
  type LocationContactRole,
} from '@/hooks/locations/useLocationContacts';

const ROLE_OPTIONS: LocationContactRole[] = ['SERVICE', 'BILLING', 'OTHER'];

export function AddLocationContactDialog({
  locationId,
  open,
  onOpenChange,
}: {
  locationId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const [mode, setMode] = useState<'new' | 'existing'>('new');
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState<{ id: string; name: string } | null>(null);
  const [title, setTitle] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [roles, setRoles] = useState<LocationContactRole[]>(['SERVICE']);
  const results = useContactSearch(search, 8, {
    enabled: open && mode === 'existing' && !selected,
  });
  const add = useAddLocationContact(locationId);
  const createContact = useCreateContact();

  const close = () => {
    setMode('new');
    setSearch('');
    setSelected(null);
    setTitle('');
    setFirstName('');
    setLastName('');
    setEmail('');
    setPhone('');
    setRoles(['SERVICE']);
    onOpenChange(false);
  };

  const attach = (contactId: string) =>
    add.mutate(
      {
        contactId,
        title: title.trim() || undefined,
        roles,
        isPrimary: true,
      },
      { onSuccess: close },
    );

  const submit = () => {
    if (mode === 'existing') {
      if (selected) attach(selected.id);
      return;
    }
    const first = firstName.trim();
    if (!first) return;
    const last = lastName.trim();
    const name = [first, last].filter(Boolean).join(' ');
    createContact.mutate(
      {
        contactType: 'RESIDENTIAL',
        firstName: first,
        ...(last ? { lastName: last } : {}),
        displayName: name,
        ...(email.trim() ? { primaryEmail: email.trim() } : {}),
        ...(phone.trim() ? { primaryPhone: phone.trim() } : {}),
      },
      {
        onSuccess: (contact) => {
          // If attaching fails, retry the saved contact instead of creating it twice.
          setSelected({ id: contact.id, name });
          setMode('existing');
          attach(contact.id);
        },
      },
    );
  };

  const rows = results.data?.data ?? [];
  const busy = add.isPending || createContact.isPending;
  const canSubmit =
    roles.length > 0 && (mode === 'existing' ? selected !== null : firstName.trim().length > 0);

  return (
    <Dialog open={open} onOpenChange={(value) => (value ? onOpenChange(true) : close())}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Add a person to this location</DialogTitle>
          <DialogDescription>
            {mode === 'new'
              ? 'Enter their details below, or add an existing contact.'
              : 'Search all contacts by name, phone or email.'}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <Button
            type="button"
            variant="link"
            className="h-auto p-0"
            disabled={busy}
            onClick={() => {
              setMode(mode === 'new' ? 'existing' : 'new');
              setSelected(null);
              setSearch('');
            }}
          >
            {mode === 'new' ? 'Add an existing contact' : 'Create a new contact'}
          </Button>

          {mode === 'new' ? (
            <div className="grid gap-3 sm:grid-cols-2">
              <div className="space-y-1">
                <Label htmlFor="lc-first">First name</Label>
                <Input
                  id="lc-first"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  disabled={busy}
                  autoFocus
                />
              </div>
              <div className="space-y-1">
                <Label htmlFor="lc-last">Last name</Label>
                <Input
                  id="lc-last"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  disabled={busy}
                />
              </div>
              <div className="space-y-1">
                <Label htmlFor="lc-phone">Phone</Label>
                <Input
                  id="lc-phone"
                  type="tel"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  disabled={busy}
                />
              </div>
              <div className="space-y-1">
                <Label htmlFor="lc-email">Email</Label>
                <Input
                  id="lc-email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  disabled={busy}
                />
              </div>
              <p className="text-xs text-muted-foreground sm:col-span-2">
                Phone and email are optional. Add them later to enable notifications.
              </p>
            </div>
          ) : selected ? (
            <div className="flex items-center justify-between rounded-md border px-3 py-2 text-sm">
              <span className="truncate">{selected.name}</span>
              <Button size="sm" variant="ghost" disabled={busy} onClick={() => setSelected(null)}>
                Change
              </Button>
            </div>
          ) : (
            <div className="space-y-2">
              <Label htmlFor="lc-search">Person</Label>
              <div className="relative">
                <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
                <Input
                  id="lc-search"
                  className="pl-8"
                  placeholder="Search by name, phone or email"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  autoFocus
                />
              </div>
              {search.trim().length >= 2 ? (
                <div className="max-h-44 overflow-y-auto rounded-md border">
                  {results.isLoading ? (
                    <p className="flex items-center gap-2 p-3 text-xs text-muted-foreground">
                      <Loader2 className="h-3 w-3 animate-spin" /> Searching…
                    </p>
                  ) : results.isError ? (
                    <p className="p-3 text-sm text-destructive">
                      Unable to search contacts. Please try again.
                    </p>
                  ) : rows.length === 0 ? (
                    <p className="p-3 text-sm text-muted-foreground">No matching contacts.</p>
                  ) : (
                    rows.map((contact) => {
                      const name = contact.displayName || contact.companyName || 'Unnamed contact';
                      return (
                        <button
                          key={contact.id}
                          type="button"
                          onClick={() => setSelected({ id: contact.id, name })}
                          className="flex w-full flex-col items-start gap-0.5 border-b px-3 py-2 text-left last:border-b-0 hover:bg-muted"
                        >
                          <span className="text-sm">{name}</span>
                          <span className="text-xs text-muted-foreground">
                            {[contact.primaryEmail, contact.primaryPhone]
                              .filter(Boolean)
                              .join(' · ') || 'No phone or email'}
                          </span>
                        </button>
                      );
                    })
                  )}
                </div>
              ) : (
                <p className="text-xs text-muted-foreground">
                  Type at least two characters to search.
                </p>
              )}
            </div>
          )}

          <div className="space-y-2">
            <Label htmlFor="lc-title">Title at this location (optional)</Label>
            <Input
              id="lc-title"
              placeholder="Site Manager, Accounts Payable…"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              disabled={busy}
            />
          </div>
          <fieldset className="space-y-2" disabled={busy}>
            <legend className="text-sm font-medium">Roles at this location</legend>
            <div className="grid gap-2 sm:grid-cols-2">
              {ROLE_OPTIONS.map((role) => (
                <label key={role} className="flex items-center gap-2 text-sm">
                  <Checkbox
                    checked={roles.includes(role)}
                    disabled={busy}
                    onCheckedChange={(checked) =>
                      setRoles((current) =>
                        ROLE_OPTIONS.filter((option) =>
                          option === role ? checked === true : current.includes(option),
                        ),
                      )
                    }
                  />
                  {ROLE_LABEL[role]}
                </label>
              ))}
            </div>
          </fieldset>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={close}>
            Cancel
          </Button>
          <Button onClick={submit} disabled={!canSubmit || busy}>
            {busy ? 'Adding…' : 'Add contact'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
